import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, RefreshCw, ChevronDown } from 'lucide-react';
import {
  OUTCOME_COLORS,
  OUTCOME_LETTERS,
  VENUE_MODES,
  MODE_SHORT,
  formEntries,
  resolveMode,
  nextMode,
  modeLabel,
} from '../../lib/teamForm';
import './gameDetails.css';

const FORM_LIMIT = 5;

const SCREEN_MODE_KEY = 'gafferpicks.formScreenMode';
// Pre-rebrand key. Read-only fallback so the Tin Pot Cup era doesn't silently
// reset a player's saved form mode the first time they load the new build.
const LEGACY_SCREEN_MODE_KEY = 'tinpot.formScreenMode';

// Mock data for demo preview
const mockFixtures = [
  { 
    id: 1, locked: false,
    fixture: { id: 101, homeTeam: { id: 1, name: 'Arsenal' }, awayTeam: { id: 2, name: 'Chelsea' }, fixtureDateTime: '2024-03-02T15:00:00' }
  },
  { 
    id: 2, locked: false,
    fixture: { id: 102, homeTeam: { id: 3, name: 'Liverpool' }, awayTeam: { id: 4, name: 'Manchester City' }, fixtureDateTime: '2024-03-02T17:30:00' }
  },
  { 
    id: 3, locked: false,
    fixture: { id: 103, homeTeam: { id: 5, name: 'Tottenham' }, awayTeam: { id: 6, name: 'Manchester United' }, fixtureDateTime: '2024-03-03T14:00:00' }
  },
  { 
    id: 4, locked: false,
    fixture: { id: 104, homeTeam: { id: 7, name: 'Newcastle' }, awayTeam: { id: 8, name: 'Aston Villa' }, fixtureDateTime: '2024-03-03T14:00:00' }
  },
];

const mockUsedTeams = [
  { id: 3, name: 'Liverpool' },
  { id: 7, name: 'Newcastle' },
];

export default function FixtureSelection({
  fixtures = mockFixtures,
  usedTeams = mockUsedTeams,
  previouslyUsedTeams: externalPreviouslyUsedTeams = null,
  currentPick = null,
  selectedTeam: externalSelectedTeam = null,
  gameWeek = 12,
  isLocked = false,
  lockedReason = '',
  submitting = false,
  onSelectTeam = null,
  onConfirmPick = null,
  onChangePick = null,
  currentPickTeamId = null,
  teamForm = {}
}) {
  // currentPickTeam: the team object for the active pick this week
  // Handles both {team: {id, name}} and flat {teamId, teamName} shapes
  // PlayerTeamSelection entities serialise team.teamName (not team.name)
  const currentPickTeam = currentPick
    ? {
        id: currentPick.team?.id ?? currentPick.teamId,
        name: currentPick.team?.teamName ?? currentPick.team?.name ?? currentPick.teamName,
        teamName: currentPick.team?.teamName ?? currentPick.team?.name ?? currentPick.teamName,
      }
    : null;
  const [internalSelectedTeam, setInternalSelectedTeam] = useState(null);
  const [internalCurrentPick, setInternalCurrentPick] = useState(currentPick);
  const [internalSubmitting, setInternalSubmitting] = useState(false);

  // Screen-level form mode persists across gameweeks and sessions (V3).
  const [screenMode, setScreenMode] = useState(() => {
    try {
      const stored = window.localStorage.getItem(SCREEN_MODE_KEY)
        ?? window.localStorage.getItem(LEGACY_SCREEN_MODE_KEY);
      return stored === 'VENUE' ? 'VENUE' : 'OVERALL';
    } catch {
      return 'OVERALL';
    }
  });

  // Per-team overrides are deliberately transient - they reset when the player leaves
  // this gameweek, so two strips side by side are never silently measuring different
  // things without the screen saying so (V5).
  const [formOverrides, setFormOverrides] = useState({});

  useEffect(() => {
    try {
      window.localStorage.setItem(SCREEN_MODE_KEY, screenMode);
    } catch {
      /* storage unavailable - mode simply won't persist */
    }
  }, [screenMode]);

  useEffect(() => {
    setFormOverrides({});
  }, [gameWeek]);

  const cycleOverride = (teamId, currentMode) => {
    setFormOverrides((prev) => ({ ...prev, [teamId]: nextMode(currentMode) }));
  };

  const isDemo = !onSelectTeam;
  const selectedTeam = isDemo ? internalSelectedTeam : externalSelectedTeam;
  const activePick = isDemo ? internalCurrentPick : currentPickTeam;

  // Exclude the current week's pick from "used" — it shows as selected (green), not greyed out
  // Use == (loose) to handle number/string type mismatches from different API responses
  const isTeamUsed = (teamId) => {
    // eslint-disable-next-line eqeqeq
    if (currentPickTeamId != null && teamId == currentPickTeamId) return false;
    // eslint-disable-next-line eqeqeq
    return usedTeams.some(t => t.id == teamId);
  };

  const handleSelectTeam = (fixtureEntry, team) => {
    if (team === null) {
      // Deselect
      if (isDemo) setInternalSelectedTeam(null);
      else onSelectTeam(null, null);
      return;
    }
    if (isDemo) setInternalSelectedTeam(team);
    else onSelectTeam(fixtureEntry, team);
  };

  const handleConfirmPick = async () => {
    if (isDemo) {
      setInternalSubmitting(true);
      await new Promise(r => setTimeout(r, 800));
      setInternalCurrentPick(internalSelectedTeam);
      setInternalSelectedTeam(null);
      setInternalSubmitting(false);
    } else {
      onConfirmPick();
    }
  };

  const handleChangePick = () => {
    if (isDemo) {
      setInternalSelectedTeam(internalCurrentPick);
      setInternalCurrentPick(null);
    } else {
      onChangePick();
    }
  };

  const isSubmitting = isDemo ? internalSubmitting : submitting;

  // Use the pre-computed list from GameDetailsPage if provided (most reliable).
  // Falls back to filtering usedTeams locally for demo/standalone use.
  // eslint-disable-next-line eqeqeq
  const previouslyUsedTeams = externalPreviouslyUsedTeams ?? usedTeams.filter(t =>
    // eslint-disable-next-line eqeqeq
    (currentPickTeamId == null || t.id != currentPickTeamId) &&
    // eslint-disable-next-line eqeqeq
    (activePick == null || t.id != activePick?.id)
  );

  const confirming = selectedTeam != null && !isLocked;

  return (
    // The dock is fixed to the viewport, so the list needs room to scroll clear of it -
    // otherwise the last fixture of the week is the one you can never read.
    <div className={`fx-root${confirming ? ' is-confirming' : ''}`}>
      {/* Section Header.
          "Pick Confirmed" once it is in, rather than "Change Your Pick": the pick is
          the achievement and the change is the rare afterthought, and leading with
          the afterthought read as though nothing had been saved. */}
      <div className="fx-heading">
        <h2 className={activePick && !isLocked ? 'is-confirmed' : undefined}>
          {isLocked ? `Week ${gameWeek} Fixtures` : activePick ? 'Pick Confirmed' : 'Make Your Pick'}
        </h2>
        {/* Once the pick is in, the line below names the team - so the subtitle stops
            repeating it and says only the part that is still actionable. */}
        <p>
          {isLocked
            ? lockedReason
            : activePick
              ? 'Tap another team if you want to change it.'
              : 'Tap a team to select them'}
        </p>
      </div>

      {/* Your pick, as one line rather than a card. It only appears once there is one -
          an empty "None yet" box was taking a fifth of a phone screen to say what the
          heading above it already said. */}
      {activePick && (
        <div className="fx-pick-line">
          <CheckCircle size={16} aria-hidden="true" />
          <span className="fx-pick-line-team">{activePick.name || activePick.teamName}</span>
          <span className="fx-pick-line-note">this week</span>
          {!isLocked && (
            <button className="fx-change-btn" onClick={handleChangePick}>Change</button>
          )}
        </div>
      )}

      {/* A team burned in an earlier week might not be playing at all this week, so the
          greyed-out rows below cannot tell the whole story on their own. Kept for that
          reason, but as a line - the bordered card was saying it twice over. */}
      {previouslyUsedTeams.length > 0 && (
        <p className="fx-used-line">
          <span className="fx-used-label">Used</span>
          {previouslyUsedTeams.map((team) => team.name || team.teamName).join(', ')}
        </p>
      )}

      {/* Form key + venue toggle.
          The key spells out that a draw eliminates you: the pips use standard football
          colours, where amber reads as a middling result, so the rule has to be carried
          in words instead of colour (D2a / F10a). The Available/Selected/Used legend that
          used to sit above this is gone - every row already says Pick, Picked or Used in
          words, so it was a key to a code nobody had to break. */}
      <div className="fx-form-key">
        <div className="fx-form-key-legend">
          {['WIN', 'DRAW', 'LOSS'].map((o) => (
            <span key={o} className="fx-pip" style={{ background: OUTCOME_COLORS[o] }}>
              {OUTCOME_LETTERS[o]}
            </span>
          ))}
          <span className="fx-form-key-text">
            oldest → newest · <strong>a draw knocks you out too</strong>
          </span>
        </div>

        <div className="fx-mode-tabs">
          {[
            { key: 'OVERALL', label: 'Overall' },
            { key: 'VENUE', label: 'Home / Away' },
          ].map(({ key, label }) => {
            const active = screenMode === key;
            return (
              <button
                key={key}
                onClick={() => setScreenMode(key)}
                title={key === 'VENUE'
                  ? 'Show each team’s record at the venue they play at this week'
                  : 'Show each team’s overall record'}
                className={`fx-mode-tab${active ? ' active' : ''}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Fixture Cards */}
      <div className="fx-list">
        {fixtures.map((fixtureEntry) => {
          // Support both flat DTO shape (entry.homeTeam) and old nested shape (entry.fixture.homeTeam)
          const fixture = fixtureEntry.fixture ?? fixtureEntry;

          const homeTeam = fixture.homeTeam;
          const awayTeam = fixture.awayTeam;
          const fixtureDateTime = fixture.fixtureDateTime;

          if (!homeTeam || !awayTeam) return null;

          const homeUsed = isTeamUsed(homeTeam.id);
          const awayUsed = isTeamUsed(awayTeam.id);
          const entryLocked = isLocked || fixtureEntry.locked;
          const homeSelected = selectedTeam?.id === homeTeam.id;
          const awaySelected = selectedTeam?.id === awayTeam.id;
          // The confirmed pick, as opposed to a team merely tapped. Both are green,
          // because to the player they are the same fact at two stages; what separates
          // them is that this one carries no confirm bar, only the word Picked.
          // eslint-disable-next-line eqeqeq
          const homePicked = activePick != null && homeTeam.id == activePick.id;
          // eslint-disable-next-line eqeqeq
          const awayPicked = activePick != null && awayTeam.id == activePick.id;

          return (
            <div key={fixtureEntry.id} className={`fx-card${entryLocked ? ' is-locked' : ''}`}>
              {/* Time Bar */}
              <div className="fx-time">
                <Clock size={14} />
                <span>
                  {fixtureDateTime
                    ? `${new Date(fixtureDateTime).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} • ${new Date(fixtureDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                    : 'Kick-off TBC'}
                </span>
              </div>

              {/* Teams Row — side by side on desktop, stacked on narrow screens.
                  See the media query at the foot of this file. */}
              <div className="fx-teams">
                <TeamButton
                  team={homeTeam}
                  isSelected={homeSelected}
                  isPicked={homePicked}
                  isUsed={homeUsed}
                  isLocked={entryLocked}
                  align="right"
                  onClick={() => !homeUsed && !entryLocked && handleSelectTeam(fixtureEntry, homeTeam)}
                  form={teamForm[homeTeam.id]}
                  formMode={resolveMode(screenMode, true, formOverrides[homeTeam.id])}
                  onCycleForm={(mode) => cycleOverride(homeTeam.id, mode)}
                />
                <div className="fx-vs">
                  vs
                </div>
                <TeamButton
                  team={awayTeam}
                  isSelected={awaySelected}
                  isPicked={awayPicked}
                  isUsed={awayUsed}
                  isLocked={entryLocked}
                  align="left"
                  onClick={() => !awayUsed && !entryLocked && handleSelectTeam(fixtureEntry, awayTeam)}
                  form={teamForm[awayTeam.id]}
                  formMode={resolveMode(screenMode, false, formOverrides[awayTeam.id])}
                  onCycleForm={(mode) => cycleOverride(awayTeam.id, mode)}
                />
              </div>
            </div>
          );
        })}
      </div>



      {confirming && (
        <ConfirmDock
          team={selectedTeam}
          onConfirm={handleConfirmPick}
          onCancel={() => handleSelectTeam(null, null)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}

/**
 * One team on one fixture, in one of four states.
 *
 * `isPicked` is the confirmed pick and `isSelected` is a team merely tapped. Both are
 * green, because to a player they are the same fact at two stages — and it was the
 * absence of the first that this whole component read wrong. Confirming cleared the
 * transient selection and nothing replaced it, so the team you had just committed to
 * for the week went back to looking exactly like the fourteen you had not, still
 * inviting you to "Pick" it. The only trace was a summary box further up the page.
 *
 * What separates the two: a selected team is the one ConfirmDock is asking about, a
 * picked one carries the word Picked and nothing to press. Tapping a picked team
 * re-selects it, which is how you get the dock back if you want to change your mind.
 */
function TeamButton({ team, isSelected, isPicked, isUsed, isLocked, align, onClick, form, formMode, onCycleForm }) {
  const disabled = isUsed || isLocked;
  const isRight = align === 'right';
  const green = isSelected || isPicked;

  // Hover is a CSS concern, guarded by @media (hover: hover) so it never applies to a
  // finger. Driven from onMouseEnter/onMouseLeave it latched on touch: tapping a team
  // left it wearing the "about to be picked" highlight until you tapped somewhere else.
  // Three states, all enumerable, so they are modifier classes rather than colours
  // computed into a style attribute. The mirrored home/away arrangement is a fourth
  // modifier, and it is the one a breakpoint needs to undo on a phone.
  const stateClass = [
    green ? 'is-green' : '',
    isUsed ? 'is-used' : '',
    isRight ? 'is-right' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="fx-team-wrap">
      <button
        className={`fx-team-btn ${stateClass}`.trim()}
        onClick={onClick}
        disabled={disabled}
      >
        <span className="fx-team-name">
          {team.name || team.teamName}
        </span>

        {/* Picked is checked before selected: re-tapping your own pick makes it both,
            and while the confirm bar is up "Picked" would be claiming the change had
            already been saved. */}
        {isSelected ? (
          <CheckCircle size={16} color="#00ff87" className="fx-tick" />
        ) : isPicked ? (
          <span className="fx-picked">
            <CheckCircle size={13} />
            Picked
          </span>
        ) : isUsed ? (
          <span className="fx-team-used">Used</span>
        ) : !isLocked ? (
          <span className="fx-pick-label">Pick</span>
        ) : null}
      </button>

      <FormPips
        form={form}
        mode={formMode}
        onCycle={onCycleForm}
        teamName={team.name || team.teamName}
      />
    </div>
  );
}

/**
 * The confirm step, docked to the bottom of the viewport while a selection is live.
 *
 * It used to render inline underneath whichever team had been tapped, which failed twice
 * on a phone. The team button squared its bottom corners to sit flush against it, but
 * FormPips renders between the two, so the join was never made. And on a ten-fixture week
 * most fixtures sit below the fold, so tapping one put the confirmation off-screen - the
 * player tapped a team and, as far as they could see, nothing happened.
 *
 * Docking it fixes both: there is no join left to break, and it cannot be scrolled away
 * from. One bar rather than one per team also means the confirm target is always in the
 * same place, which is what makes it findable without looking.
 */
function ConfirmDock({ team, onConfirm, onCancel, isSubmitting }) {
  const name = team.name || team.teamName;

  return (
    <div className="fx-confirm-dock" role="region" aria-label="Confirm your pick">
      <div className="fx-confirm-inner">
        <button
          type="button"
          className="fx-confirm-btn"
          onClick={onCancel}
          aria-label={`Cancel selecting ${name}`}
        >
          ✕
        </button>
        <span className="fx-confirm-q">Confirm {name}?</span>
        <button
          type="button"
          className="fx-confirm-btn"
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? <RefreshCw size={16} className="fx-spin" />
            : <CheckCircle size={16} />}
          {isSubmitting ? 'Saving...' : 'Yes'}
        </button>
      </div>
    </div>
  );
}


/**
 * Form strip under a team's name on the pick screen.
 *
 * Shows the results that exist and nothing else: a team with two results shows two pips,
 * and one with none says so in words. It used to pad to five with greyed placeholders
 * (D4/F17), which the owner reversed on 3 Nov 2026 (backlog item 87). Rows stay the same
 * height without them because the strip carries its own min-height (.fx-form-strip).
 *
 * The mode label doubles as the per-team toggle (V4/V6). It sits outside the pick
 * button and stops propagation, so cycling it can never fire a pick (V7) - a pick is
 * expensive, it burns that team for the rest of the tournament.
 */
function FormPips({ form, align, mode = VENUE_MODES.OVERALL, onCycle, teamName }) {
  const [open, setOpen] = useState(false);
  const entries = formEntries(form, mode, FORM_LIMIT);

  // Most-recent-first from the API; reverse so the strip reads oldest -> newest.
  const shown = [...entries].reverse();

  const venueWord = mode === VENUE_MODES.HOME ? 'home' : mode === VENUE_MODES.AWAY ? 'away' : '';
  const emptyWord = mode === VENUE_MODES.OVERALL ? 'All' : venueWord;
  const emptyHint = venueWord ? `No ${venueWord} results yet` : 'No results yet';
  const label = entries.length > 0 ? modeLabel(mode, entries.length) : emptyHint;

  const hasResults = entries.length > 0;

  const strip = hasResults ? (
    <span className="fx-form-pips">
      {shown.map((entry, i) => (
        <span
          key={entry.fixtureId ?? `f-${i}`}
          className="fx-pip"
          style={{ background: OUTCOME_COLORS[entry.outcome] || '#ccc' }}
        >
          {OUTCOME_LETTERS[entry.outcome] || '?'}
        </span>
      ))}
    </span>
  ) : (
    <span className="fx-form-none">{emptyHint}</span>
  );

  // The venue word sits beside real pips; with none, the hint already names the venue.
  const modeWord = hasResults ? <span className="fx-form-mode">{emptyWord}</span> : null;

  // Nothing to open on a read-only render (the demo), so it stays a plain strip.
  if (!onCycle) {
    return (
      <div className="fx-form">
        {strip}
        {modeWord}
      </div>
    );
  }

  return (
    <div className="fx-form">
      <button
        type="button"
        className="fx-form-strip"
        aria-expanded={open}
        aria-label={`${label} for ${teamName}. Show each result.`}
        onClick={(e) => {
          // A pick is expensive - it burns that team for the rest of the tournament -
          // so nothing in this strip may ever reach the pick button behind it.
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        {strip}
        {modeWord}
        <ChevronDown size={13} className={`fx-form-caret${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="fx-form-detail">
          {entries.length === 0 ? (
            <p className="fx-form-empty">{emptyHint}</p>
          ) : (
            <ul className="fx-form-results">
              {/* Newest first here: read as a list, the most recent result is the one
                  you want at the top, which is the opposite of the strip's reading. */}
              {entries.map((entry, i) => (
                <li key={entry.fixtureId ?? `d-${i}`}>
                  <span
                    className="fx-pip fx-form-result-pip"
                    style={{ background: OUTCOME_COLORS[entry.outcome] || '#ccc' }}
                  >
                    {OUTCOME_LETTERS[entry.outcome] || '?'}
                  </span>
                  <span className="fx-form-opponent">v {entry.opponentName}</span>
                  {entry.teamScore != null && entry.opponentScore != null && (
                    <span className="fx-form-score">{entry.teamScore}–{entry.opponentScore}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* The venue switch lives in here rather than beside the strip. It used to be
              a 25x13px button sitting directly under the pick button - the smallest
              target on the screen, next to the one action a player cannot take back. */}
          <button
            type="button"
            className="fx-form-mode-btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCycle(mode);
            }}
          >
            Showing {MODE_SHORT[mode].toLowerCase()} — switch
          </button>
        </div>
      )}
    </div>
  );
}